import React, { useMemo } from 'react';
import { useApp } from '../../context/SupabaseAppContext';
import { Idea, RoadmapItem } from '../../types';
import { 
  CalendarIcon,
  UserIcon,
  StarIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRoute } from '@fortawesome/free-solid-svg-icons';
import { format, addMonths, startOfQuarter, endOfQuarter } from 'date-fns';

interface RoadmapProps {
  onPageChange?: (page: string) => void;
  onEditIdea?: (idea: Idea) => void;
}

const Roadmap: React.FC<RoadmapProps> = ({ onPageChange, onEditIdea }) => {
  const { state } = useApp();

  // Get all "In Progress" ideas and existing roadmap items
  const inProgressIdeas = useMemo(() => {
    return state.ideas.filter(idea => idea.status === 'In Progress');
  }, [state.ideas]);

  const roadmapItems = useMemo(() => {
    return state.roadmapItems.filter(item => item.status === 'In Progress');
  }, [state.roadmapItems]);

  // Generate quarters based on the predefined quarter options
  const quarters = useMemo(() => {
    const quarterOptions = ['Q3 2025', 'Q4 2025', 'Q1 2026', 'Q2 2026', 'Q3 2026', 'Q4 2026', 'Q1 2027', 'Q2 2027', 'Q3 2027'];
    const quarterData: Array<{
      id: string;
      label: string;
      start: Date;
      end: Date;
      ideas: Idea[];
      roadmapItems: RoadmapItem[];
    }> = [];
    
    quarterOptions.forEach(quarterLabel => {
      // Parse quarter and year from the label
      const [quarter, year] = quarterLabel.split(' ');
      const quarterNum = parseInt(quarter.substring(1));
      const yearNum = parseInt(year);
      
      // Create date range for the quarter
      const quarterStart = new Date(yearNum, (quarterNum - 1) * 3, 1);
      const quarterEnd = new Date(yearNum, quarterNum * 3, 0);
      
      // Filter ideas by their planned quarter
      const quarterIdeas = inProgressIdeas.filter((idea: Idea) => idea.plannedQuarter === quarterLabel);
      
      // Filter roadmap items by their quarter
      const quarterRoadmapItems = roadmapItems.filter((item: RoadmapItem) => item.quarter === quarterLabel);
      
      quarterData.push({
        id: `${yearNum}-Q${quarterNum}`,
        label: quarterLabel,
        start: quarterStart,
        end: quarterEnd,
        ideas: quarterIdeas,
        roadmapItems: quarterRoadmapItems
      });
    });
    
    return quarterData;
  }, [inProgressIdeas, roadmapItems]);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Feature': return 'bg-energetic-blue';
      case 'Product': return 'bg-electric-lime';
      case 'Process': return 'bg-orange-500';
      case 'Technology': return 'bg-purple-500';
      case 'Sales and Marketing': return 'bg-pink-500';
      case 'Innovation': return 'bg-deep-teal';
      default: return 'bg-gray-500';
    }
  };

  const RoadmapCard: React.FC<{ idea?: Idea; item?: RoadmapItem }> = ({ idea, item }) => {
    const title = idea?.title || item?.title || '';
    const description = idea?.description || item?.description || '';
    const category = idea?.category || item?.category || '';
    const owner = idea?.owner || item?.assignee;
    const progress = item?.progress || 0;
    
    const handleCardClick = () => {
      // Only allow editing of ideas (not roadmap items) and only if onEditIdea is provided
      if (idea && onEditIdea) {
        onEditIdea(idea);
      }
    };
    
    return (
      <div 
        className={`bg-white rounded-lg border border-gray-200 p-4 transition-all duration-200 ${
          idea && onEditIdea 
            ? 'hover:shadow-md hover:border-deep-teal cursor-pointer transform hover:-translate-y-1' 
            : 'hover:shadow-md'
        }`}
        onClick={handleCardClick}
        title={idea && onEditIdea ? 'Click to edit this idea' : ''}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h4 className="font-semibold text-gray-900">{title}</h4>
              {idea && (
                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-mono">
                  {idea.uniqueId}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 line-clamp-2">{description}</p>
          </div>
          <div className={`w-3 h-3 rounded-full ml-3 mt-1 ${getCategoryColor(category)}`}></div>
        </div>
        
        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
          <div className="flex items-center">
            <UserIcon className="h-4 w-4 mr-1" />
            <span>{owner?.name}</span>
          </div>
          {idea && (
            <div className="flex items-center">
              <StarIcon className="h-4 w-4 mr-1 text-yellow-400" />
              <span className="font-medium">{idea.score?.toFixed(1)}</span>
            </div>
          )}
        </div>

        {item && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-deep-teal h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            category === 'Feature' ? 'badge-primary' :
            category === 'Product' ? 'badge-success' :
            category === 'Process' ? 'bg-orange-100 text-orange-800' :
            category === 'Technology' ? 'bg-purple-100 text-purple-800' :
            category === 'Sales and Marketing' ? 'bg-pink-100 text-pink-800' :
            'badge-info'
          }`}>
            {category}
          </span>
          
          {item && (
            <div className="flex items-center text-xs text-gray-500">
              <CalendarIcon className="h-3 w-3 mr-1" />
              <span>
                {item.startDate && format(new Date(item.startDate), 'MMM d')} - 
                {item.endDate && format(new Date(item.endDate), 'MMM d')}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faRoute} className="mr-3 text-deep-teal" />
            Product Roadmap
          </h1>
          <p className="text-gray-600 mt-2">
            In-Progress Ideas Appear on the Roadmap
          </p>
        </div>

      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <ClockIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">{inProgressIdeas.length}</h3>
              <p className="text-sm text-gray-600">Ideas in Progress</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-deep-teal bg-opacity-10">
              <ChartBarIcon className="h-6 w-6 text-deep-teal" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">{roadmapItems.length}</h3>
              <p className="text-sm text-gray-600">Active Projects</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-electric-lime bg-opacity-20">
              <StarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {inProgressIdeas.reduce((sum, idea) => sum + (idea.score || 0), 0).toFixed(1)}
              </h3>
              <p className="text-sm text-gray-600">Total Score</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-energetic-blue bg-opacity-10">
              <UserIcon className="h-6 w-6 text-energetic-blue" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {new Set([...inProgressIdeas.map(i => i.owner.id), ...roadmapItems.map(r => r.assignee?.id)]).size}
              </h3>
              <p className="text-sm text-gray-600">Contributors</p>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline View */}
      <div className="space-y-8">
          {quarters.map((quarter) => (
            <div key={quarter.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-deep-teal to-energetic-blue px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">{quarter.label}</h3>
                  <div className="text-white text-sm">
                    {format(quarter.start, 'MMM d')} - {format(quarter.end, 'MMM d')}
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {quarter.ideas.length > 0 || quarter.roadmapItems.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {quarter.ideas.map((idea) => (
                      <RoadmapCard key={`idea-${idea.id}`} idea={idea} />
                    ))}
                    {quarter.roadmapItems.map((item) => (
                      <RoadmapCard key={`item-${item.id}`} item={item} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No items planned</h4>
                    <p className="text-gray-500">No ideas or projects scheduled for {quarter.label}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
                </div>

      {/* Empty State */}
      {inProgressIdeas.length === 0 && roadmapItems.length === 0 && (
        <div className="text-center py-16">
          <FontAwesomeIcon icon={faRoute} className="h-16 w-16 text-gray-400 mx-auto mb-6" />
          <h3 className="text-xl font-medium text-gray-900 mb-4">No items in progress</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Start by moving some ideas to "In Progress" status or create roadmap items to see them here.
          </p>
          <button 
            onClick={() => onPageChange?.('ideas')}
            className="btn-primary flex items-center mx-auto"
          >
            <ArrowRightIcon className="h-5 w-5 mr-2" />
            Manage Ideas
          </button>
        </div>
      )}
    </div>
  );
};

export default Roadmap; 